// ---------------------------------------------------------------------------
// The socket connection.
//
// Three behaviours here are load-bearing for fairness, not polish:
//
//   1. A 1s heartbeat, so the server knows when it last heard from us. Our
//      effective deadline is min(round deadline, last heartbeat + 1.5s).
//   2. Draft streaming on a 300ms debounce, so a disconnect costs a few hundred
//      milliseconds of typing rather than the whole round.
//   3. The rendered clock freezes ONLY on a server pause, never on our own
//      disconnect. Freezing our own clock while everyone else's runs would
//      show us time we do not actually have.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  DRAFT_DEBOUNCE_MS,
  HEARTBEAT_INTERVAL_MS,
  RECONNECT_BACKOFF_START_MS,
} from '@countdown/shared';
import type {
  ClientMessage,
  NumbersStep,
  ServerErrorCode,
  ServerMessage,
} from '@countdown/shared';
import { useGameStore } from '../store/gameStore.js';
import { usePlayerStore } from '../store/playerStore.js';
import { useSettingsStore } from '../store/settingsStore.js';

export interface JoinOptions {
  roomCode: string;
  turnstileToken?: string;
  /**
   * Mint a Clerk session token for a join. A function rather than a string,
   * because the token has to be fetched at the moment it is sent: one lives
   * about a minute, and a join is sent again on every socket.io reconnect.
   *
   * A string settled once per page was expired by its second use, and — worse —
   * was permanently `undefined` for anybody whose page loaded before Clerk
   * answered. Undefined, and a call that rejects, are both an ordinary guest
   * join. Signing in is never required for anything.
   */
  getClerkToken?: () => Promise<string | undefined>;
  asSpectator?: boolean;
  /**
   * Whether the account layer currently reports a session. Read only to notice
   * a sign-in that lands with the socket already open, which re-sends the join
   * so the server can upgrade the seat. The token itself always comes from
   * `getClerkToken`.
   */
  signedIn?: boolean;
}

export interface GameConnection {
  send: (message: ClientMessage) => void;
  /**
   * Send whatever draft is sitting in the debounce, now.
   *
   * The throttle is trailing-edge, so the last keystroke before a deadline can
   * be up to `DRAFT_DEBOUNCE_MS` from reaching the server — and that keystroke
   * is precisely the one a deadline-driven fallback needs. A caller that knows
   * a window is about to close calls this; nobody else has to think about it.
   */
  flushDrafts: () => void;
  /** Stream an in-progress word. Debounced; call it on every keystroke. */
  streamWord: (word: string) => void;
  streamSteps: (steps: NumbersStep[]) => void;
  /**
   * Stream an in-progress conundrum answer, on the same throttle as the other
   * two. It exists because a buzzed player who typed the right answer and was a
   * moment slow pressing submit was scored as having typed nothing: the server
   * had no copy of it to fall back on.
   *
   * Send-only. Unlike `streamWord` it is deliberately not written to the
   * persisted player store: a half-typed conundrum answer is the single most
   * valuable thing in the room, and there is no reason for it to be on this
   * device's disk.
   */
  streamConundrum: (answer: string) => void;
  connected: () => boolean;
}

/**
 * What a server `error` frame becomes.
 *
 * **The rule used to be "have we got state or not", and that was the bug.** An
 * error before any state is a failed join and belongs in the join form; an error
 * afterwards was a toast, unconditionally. That is right for every error the
 * server sends about something the player TRIED — a lock a moment late, a word
 * the board cannot build — because the room is still there to try again in.
 *
 * It is wrong for exactly one code. A room that is gone is not a refusal: there
 * is nothing to try again and no state to be in. Isaac reported what that looked
 * like from a phone — back out to another app, come back, and a toast says the
 * room is not there while the board, the clock and the players strip are all
 * still drawn behind it, none of them doing anything.
 *
 * **It reaches a client precisely BECAUSE the `room-closed` broadcast did not.**
 * That broadcast goes only to connected sockets, and the sweep that closes an
 * empty room fires *because* nobody is connected — so for the commonest reason a
 * room dies, it reaches nobody at all, and the error from the rejoin is the only
 * way anybody is ever told. Which is also why the reason line below can say
 * "while you were away" and be true: getting here means the broadcast was
 * missed, and missing it means this client was not connected when it went.
 *
 * Exported for its test. It is the whole of the decision, so testing it is
 * testing the rule rather than a socket harness.
 */
export type ErrorOutcome = 'join-form' | 'dead-end' | 'toast';

export function errorOutcome(code: ServerErrorCode | undefined, hasState: boolean): ErrorOutcome {
  if (!hasState) return 'join-form';
  return code === 'room-gone' ? 'dead-end' : 'toast';
}

/**
 * What the dead-end card says under `The room closed` on this path.
 *
 * The room is gone, so the server no longer holds the reason it went — the
 * broadcast carries `Everyone left.` or the idle sentence, and this path is the
 * one where that broadcast was missed. This is what is still true and still
 * useful without it.
 */
export const ROOM_GONE_REASON = 'It closed while you were away.';

export function useGameConnection(options: JoinOptions | null): GameConnection {
  const socketRef = useRef<Socket | null>(null);
  const sequenceRef = useRef(0);
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDraftRef = useRef<ClientMessage | null>(null);
  const lastPingRef = useRef<number>(0);

  // The join is sent from more than one moment — every socket.io reconnect, and
  // a sign-in that lands with the socket already open — so what it sends is read
  // through a ref. In the effect's dependencies instead, a token function that
  // Clerk re-creates, or a sign-in, would tear the socket down and rebuild it
  // mid-round.
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  /** Re-send the join on the open socket, set while one exists. */
  const rejoinRef = useRef<(() => void) | null>(null);

  const send = useCallback((message: ClientMessage) => {
    socketRef.current?.emit('message', message);
  }, []);

  /** Debounce so a fast typist sends a few bytes per second, not per keystroke. */
  const flushDraft = useCallback(() => {
    const pending = pendingDraftRef.current;
    pendingDraftRef.current = null;
    // Cancel the scheduled run as well as forgetting it. Called by hand this
    // would otherwise leave a timer that fires into an empty queue and, worse,
    // lets the next `queueDraft` schedule a second one alongside it.
    if (draftTimerRef.current !== null) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = null;
    if (!pending) return;
    socketRef.current?.emit('message', pending);
    // Marked on the way out rather than on the way in: a draft still sitting in
    // the debounce is not yet the server's problem, and counting it as awaiting
    // acknowledgement would report every keystroke as unsaved.
    if ('sequence' in pending) useGameStore.getState().markDraftSent(pending.sequence);
  }, []);

  const queueDraft = useCallback(
    (message: ClientMessage) => {
      pendingDraftRef.current = message;
      if (draftTimerRef.current === null) {
        draftTimerRef.current = setTimeout(flushDraft, DRAFT_DEBOUNCE_MS);
      }
    },
    [flushDraft],
  );

  const streamWord = useCallback(
    (word: string) => {
      const roomCode = options?.roomCode;
      if (roomCode) usePlayerStore.getState().setDraft(roomCode, word);
      queueDraft({ type: 'draft-word', word, sequence: ++sequenceRef.current });
    },
    [options?.roomCode, queueDraft],
  );

  const streamSteps = useCallback(
    (steps: NumbersStep[]) => {
      queueDraft({ type: 'draft-steps', steps, sequence: ++sequenceRef.current });
    },
    [queueDraft],
  );

  // One counter across all three kinds, because the acknowledgement it comes
  // back on is one counter too. A second sequencing scheme here would leave the
  // sync indicator comparing numbers from two different rulers.
  const streamConundrum = useCallback(
    (answer: string) => {
      queueDraft({ type: 'draft-conundrum', answer, sequence: ++sequenceRef.current });
    },
    [queueDraft],
  );

  // Destructured out here so the socket effect never reads `options` itself, and
  // therefore never restarts because the object identity changed.
  const roomCode = options?.roomCode;
  const turnstileToken = options?.turnstileToken;
  const asSpectator = options?.asSpectator;
  const signedIn = options?.signedIn === true;

  useEffect(() => {
    if (roomCode === undefined) return;

    const store = useGameStore.getState();

    const socket = io({
      query: { room: roomCode },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: RECONNECT_BACKOFF_START_MS,
      reconnectionDelayMax: 5_000,
      timeout: 10_000,
    });
    socketRef.current = socket;

    const join = async () => {
      const live = optionsRef.current;
      // A token that will not come is a guest join, never a failed one, so this
      // is the one await in the path and it cannot reject.
      const clerkToken = await live?.getClerkToken?.().catch(() => undefined);
      // Read after the await rather than before it: the store is where the
      // reconnect token the server just issued lands, and this is the field that
      // decides whether the server sees a new player or the same seat again.
      const current = usePlayerStore.getState();
      const message: ClientMessage = {
        type: 'join',
        displayName: current.displayName,
        deviceId: current.deviceId,
        reconnectToken: current.reconnectTokens[roomCode],
        turnstileToken: live?.turnstileToken,
        clerkToken,
        asSpectator: live?.asSpectator,
        preferredColor: current.preferredColor ?? undefined,
      };
      socket.emit('message', message);
    };

    // Only ever on a live socket. Buffered by socket.io instead, a re-join fired
    // before the first connect would arrive alongside the one `connect` sends.
    rejoinRef.current = () => {
      if (socket.connected) void join();
    };

    socket.on('connect', () => {
      store.setQuality({ status: 'connected' });

      // A reconnect is deliberately NOT treated as the deploy having landed.
      // It used to be, and the claim does not hold: the same socket reconnects
      // when a phone changes cells or a laptop wakes up, and an announcement
      // lingers well past the moment it named, so an ordinary blip during a
      // deploy window declared victory and hard-reloaded the page under
      // somebody mid-round. `useSiteConfig` decides this, by watching for a
      // buildId that has actually changed.
      void join();
    });

    socket.on('disconnect', () => {
      // Our own disconnect does NOT freeze the clock. The overlay says the round
      // is still running and the answer is saved, which is true.
      useGameStore.getState().setQuality({ status: 'reconnecting' });
    });

    socket.on('connect_error', () => {
      useGameStore.getState().setQuality({ status: 'reconnecting' });
    });

    socket.on('message', (message: ServerMessage) => {
      const state = useGameStore.getState();

      switch (message.type) {
        case 'room-state':
          state.setState(message.state, message.serverTime);
          usePlayerStore.getState().setReconnectToken(roomCode, message.reconnectToken);
          state.setQuality({
            status: message.state.serverPaused ? 'server-paused' : 'connected',
          });
          return;

        case 'pong': {
          const rtt = Date.now() - message.clientTime;
          state.setQuality({ rttMs: rtt });
          return;
        }

        case 'draft-ack':
          state.markDraftAcked(message.sequence, message.at);
          return;

        // The ruling on one word this player offered, sent to them alone. It
        // carries the word back because a fast typist has two in flight, and
        // `actionId` is what actually attaches it to the row that asked.
        case 'word-result':
          state.settleWord({
            word: message.word,
            valid: message.valid,
            score: message.score,
            adopted: message.adopted,
            actionId: message.actionId,
          });
          return;

        // Every one of these is a paced sequence the room state already carries,
        // so they are received and dropped. The letters board is on `round.tiles`
        // and the selection screen releases it on its own metronome; the reveal
        // is paced by the server and carried on the round too. Both used to be
        // accumulated into a second copy in the store, and a second copy of a
        // paced sequence is what let two clients disagree about what had already
        // arrived. Do not start buffering them here again.
        case 'letter-revealed':
        case 'letters-auto-filled':
        case 'declared-lengths':
        case 'reveal-step':
        case 'reveal-best':
        case 'phase-changed':
          return;

        // What the game just played cost. It is not on `room-state` and cannot
        // be — the figure is read out of the spend ledger and the engine that
        // builds the state is pure — so it arrives on its own, at game-over and
        // again on every socket that arrives while the room is still on the end
        // screen. That second delivery is the whole of the reload case: this
        // store is not persisted, so a refresh has nothing left of the first.
        case 'game-cost':
          state.setGameCost({ gameId: message.gameId, cost: message.cost });
          return;

        case 'chat-message':
          state.appendChat(message.message);
          return;

        case 'log-entry':
          state.appendLog(message.entry);
          return;

        // A server pause is the one case where the rendered clock freezes.
        case 'server-paused':
          state.setQuality({ status: 'server-paused' });
          return;

        case 'server-resumed':
          state.setQuality({ status: 'connected' });
          return;

        // The deploy workflow announces a restart before it triggers one. This
        // case did not exist, so the message was received and dropped, and the
        // warning the whole announce-then-wait dance exists to deliver reached
        // nobody.
        case 'maintenance':
          state.announceMaintenance({
            message: message.message,
            deployAt: message.deployAt,
          });
          return;

        // Four ways out of a room that are not a connection problem. The
        // reason travels with the wipe, because otherwise being kicked is
        // indistinguishable from a socket that never came up.
        case 'kicked':
          usePlayerStore.getState().clearReconnectToken(roomCode);
          socket.disconnect();
          state.clear({ kind: 'kicked', reason: message.reason });
          return;

        case 'banned':
          usePlayerStore.getState().clearReconnectToken(roomCode);
          socket.disconnect();
          state.clear({ kind: 'banned', reason: message.reason });
          return;

        case 'room-closed':
          usePlayerStore.getState().clearReconnectToken(roomCode);
          socket.disconnect();
          state.clear({ kind: 'room-closed', reason: message.reason });
          return;

        // The fourth way out, and the only one that keeps the token. This socket
        // was replaced by the same player joining from another tab or device, so
        // the seat is still theirs and rejoining from here takes it back —
        // clearing the token would turn a second tab into a lost seat.
        //
        // Disconnecting by hand is what stops the reconnect overlay: without it
        // the server's own close reads as a dropped connection, and the player
        // gets the whole "the round is still running" sheet for a room they are
        // sitting in on the device in front of them.
        case 'displaced':
          socket.disconnect();
          state.clear({ kind: 'displaced', reason: null });
          return;

        case 'error': {
          const outcome = errorOutcome(
            message.code,
            useGameStore.getState().state !== null,
          );
          if (outcome === 'join-form') {
            state.setJoinError({ message: message.message, code: message.code });
            return;
          }
          if (outcome === 'dead-end') {
            // Not the server's own sentence. `That room code does not exist.`
            // under a heading reading `The room closed` is the same fact twice
            // and answers none of what somebody coming back wants to know.
            state.clear({ kind: 'room-closed', reason: ROOM_GONE_REASON });
            return;
          }
          // Alert, not neutral: the server is saying something the player tried
          // did not land. A grey card for that reads as news.
          state.pushToast(message.message, 'alert');
          return;
        }

        // The other half of that: a line that is not a refusal. It carries its
        // own tone rather than having one inferred, which is what "Report sent.
        // Thanks." needed — it travelled on `error` and arrived in alert red.
        case 'notice':
          state.pushToast(message.message, message.tone);
          return;

        default:
          return;
      }
    });

    // The heartbeat is what makes a wifi blip nearly costless. Keep it running
    // even while a round is not live, so a reconnect mid-round has a recent
    // last-verified-alive to work from.
    const heartbeat = setInterval(() => {
      if (socket.connected) socket.emit('message', { type: 'heartbeat' } satisfies ClientMessage);
    }, HEARTBEAT_INTERVAL_MS);

    // Ping less often: it exists for the conundrum buzz compensation and the
    // connection indicator, neither of which needs one-second resolution.
    const ping = setInterval(() => {
      if (!socket.connected) return;
      lastPingRef.current = Date.now();
      socket.emit('message', { type: 'ping', clientTime: lastPingRef.current } satisfies ClientMessage);
    }, 4_000);

    // Admin mode is opt-in and per-session, so attach only when it is on.
    const settings = useSettingsStore.getState();
    if (settings.adminMode && settings.adminSessionToken) {
      socket.on('connect', () => {
        socket.emit('message', {
          type: 'admin-attach',
          sessionToken: settings.adminSessionToken as string,
        } satisfies ClientMessage);
      });
    }

    return () => {
      clearInterval(heartbeat);
      clearInterval(ping);
      // Send whatever is still sitting in the throttle before the socket goes,
      // rather than dropping it with the timer. The keystroke this saves is the
      // last one before a screen closes — which, for a conundrum answer typed
      // against a ten second window, is exactly the keystroke that matters.
      if (draftTimerRef.current) {
        clearTimeout(draftTimerRef.current);
        flushDraft();
      }
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      rejoinRef.current = null;
    };
  }, [roomCode, turnstileToken, asSpectator, flushDraft]);

  // Signing in while already sitting in a room. The seat was created as a guest
  // and the session token only ever travels on a join, so without this the
  // server is never told and the seat stays a guest until the page is reloaded.
  // Re-sending is safe rather than a second join: the reconnect token finds the
  // same seat, and the server upgrades the identity on it in place.
  const wasSignedIn = useRef(signedIn);
  useEffect(() => {
    const gained = signedIn && !wasSignedIn.current;
    wasSignedIn.current = signedIn;
    if (gained) rejoinRef.current?.();
  }, [signedIn]);

  const connected = useCallback(() => socketRef.current?.connected ?? false, []);

  return { send, flushDrafts: flushDraft, streamWord, streamSteps, streamConundrum, connected };
}
